/**
 * PostHog 分析工具
 * 提供统一的埋点和用户识别功能
 */
import { Posthog } from '@capawesome/capacitor-posthog';
import { logger } from './logger';

/**
 * 远程配置结果接口
 */
export interface RemoteConfigResult {
  url: string;
}

/**
 * iOS版本配置结果接口
 */
export interface IOSVersionConfigResult {
  version: string;
}

/**
 * 统一版本配置结果接口
 */
export interface UnifiedVersionConfigResult {
  version: string;
}

/**
 * 远程配置缓存前缀
 */
const REMOTE_CONFIG_CACHE_PREFIX = 'posthog_remote_config_';

/**
 * 缓存过期时间（1小时）
 */
const CACHE_EXPIRY_MS = 60 * 60 * 1000;

/**
 * 应用版本
 * 从环境变量中获取，如果不存在则使用 'unknown'
 */
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown';

/**
 * 当前应用版本（v0 或 v3）
 * 根据 URL 路径判断
 */
const getCurrentVersion = (): 'v0' | 'v3' => {
  const path = window.location.pathname;
  return path.startsWith('/v3') ? 'v3' : 'v0';
};

/**
 * 通用事件跟踪函数
 * 
 * @param eventName 事件名称，建议使用 '[object] [verb]' 格式
 * @param properties 事件属性
 */
export const trackEvent = async (
  eventName: string,
  properties: Record<string, any> = {}
): Promise<void> => {
  try {
    // 添加通用属性
    const commonProps = {
      app_version: APP_VERSION,
      ui_version: getCurrentVersion(),
      ...properties
    };

    // 发送事件到 PostHog
    await Posthog.capture({
      event: eventName,
      properties: commonProps
    });

    // 记录到日志（仅开发环境）
    if (import.meta.env.DEV) {
      logger.debug(`[Analytics] Tracked: ${eventName}`, commonProps);
    }
  } catch (error) {
    logger.error(`[Analytics] Failed to track event: ${eventName}`, { error, properties });
  }
};

/**
 * 页面访问跟踪
 * 
 * @param pageName 页面名称
 * @param properties 额外属性
 */
export const trackPageView = async (
  pageName: string,
  properties: Record<string, any> = {}
): Promise<void> => {
  await trackEvent('page viewed', {
    page_name: pageName,
    ...properties
  });
};

/**
 * 用户识别
 * 当用户登录后调用此函数
 * 
 * @param userId 用户ID
 * @param userProperties 用户属性
 */
export const identifyUser = async (
  userId: string,
  userProperties: Record<string, any> = {}
): Promise<void> => {
  try {
    await Posthog.identify({
      distinctId: userId,
      userProperties: {
        ...userProperties,
        app_version: APP_VERSION,
      }
    });

    if (import.meta.env.DEV) {
      logger.debug(`[Analytics] User identified: ${userId}`);
    }
  } catch (error) {
    logger.error(`[Analytics] Failed to identify user: ${userId}`, { error });
  }
};

/**
 * 重置用户
 * 当用户退出登录时调用此函数
 */
export const resetUser = async (): Promise<void> => {
  try {
    await Posthog.reset();
    
    if (import.meta.env.DEV) {
      logger.debug('[Analytics] User reset');
    }
  } catch (error) {
    logger.error('[Analytics] Failed to reset user', { error });
  }
};

/**
 * 设置组属性
 * 用于关联用户与特定组（如团队、组织等）
 * 
 * @param type 组类型
 * @param key 组ID
 * @param groupProperties 组属性
 */
export const setGroup = async (
  type: string,
  key: string,
  groupProperties: Record<string, any> = {}
): Promise<void> => {
  try {
    await Posthog.group({
      type,
      key,
      groupProperties
    });
    
    if (import.meta.env.DEV) {
      logger.debug(`[Analytics] Group set: ${type}:${key}`);
    }
  } catch (error) {
    logger.error(`[Analytics] Failed to set group: ${type}:${key}`, { error });
  }
};

/**
 * 刷新事件队列
 * 强制发送所有队列中的事件
 */
export const flushEvents = async (): Promise<void> => {
  try {
    await Posthog.flush();

    if (import.meta.env.DEV) {
      logger.debug('[Analytics] Events flushed');
    }
  } catch (error) {
    logger.error('[Analytics] Failed to flush events', { error });
  }
};

/**
 * 获取远程配置
 * 从 PostHog Feature Flag 获取配置值，支持缓存
 *
 * @param key 配置键名
 * @returns 配置值，获取失败时返回 null
 */
export const getRemoteConfig = async <T = any>(key: string): Promise<T | null> => {
  try {
    // 检查环境变量是否启用远程配置
    const isEnabled = import.meta.env.VITE_POSTHOG_REMOTE_CONFIG_ENABLED !== 'false';
    if (!isEnabled) {
      if (import.meta.env.DEV) {
        logger.debug(`[Analytics] Remote config disabled for key: ${key}`);
      }
      return null;
    }

    // 检查缓存
    const cacheKey = `${REMOTE_CONFIG_CACHE_PREFIX}${key}`;
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const isExpired = Date.now() - parsed.timestamp > CACHE_EXPIRY_MS;

        if (!isExpired) {
          if (import.meta.env.DEV) {
            logger.debug(`[Analytics] Using cached remote config for key: ${key}`, parsed.value);
          }
          return parsed.value;
        } else {
          // 清除过期缓存
          localStorage.removeItem(cacheKey);
        }
      } catch (parseError) {
        // 缓存数据格式错误，清除缓存
        localStorage.removeItem(cacheKey);
        logger.warn(`[Analytics] Invalid cached data for key: ${key}`, { parseError });
      }
    }

    // 从 PostHog 获取配置
    const result = await Posthog.getFeatureFlagPayload({ key });

    if (result && result.value) {
      // 缓存结果
      const cacheData = {
        value: result.value,
        timestamp: Date.now()
      };

      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (storageError) {
        logger.warn(`[Analytics] Failed to cache remote config for key: ${key}`, { storageError });
      }

      if (import.meta.env.DEV) {
        logger.debug(`[Analytics] Retrieved remote config for key: ${key}`, result.value);
      }

      return result.value as T;
    }

    if (import.meta.env.DEV) {
      logger.debug(`[Analytics] No remote config found for key: ${key}`);
    }
    return null;

  } catch (error) {
    logger.error(`[Analytics] Failed to get remote config for key: ${key}`, { error });
    return null;
  }
};
